// @ts-check

/**
 * Bundle Picker — behaviour for `blocks/bundle-picker.liquid`.
 *
 * Pick one colourway in group "a" and one in group "b"; the price panel is
 * revealed and priced at (a + b) minus the block's percentage discount. Add to
 * Cart posts BOTH component variants, each stamped with the shared
 * `_sgbundle_*` properties, so the `bundle-cart-transform` Shopify Function
 * merges them into one line presented as the bundle product's own variant
 * (`_sgbundle_parent`) at `_sgbundle_price`.
 *
 * No parent line is added: the transform re-presents the merged children AS the
 * parent, so adding it too would make the parent a component of itself. Same
 * contract as `assets/fixed-bundle.js`.
 */

const GROUPS = ['a', 'b'];

/**
 * The storefront is single-currency USD (`${{amount}}`), which is what the
 * original block assumed too.
 * @param {number} cents
 */
function formatMoney(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

/** @param {HTMLElement} root */
function initBundlePicker(root) {
  if (root.dataset.bpInit) return;
  root.dataset.bpInit = 'true';

  const discountPct = parseFloat(root.dataset.discountPct || '0') || 0;
  const parentVariant = root.dataset.parentVariant || '';
  const productId = root.dataset.productId || '';
  const bundleHandle = root.dataset.bundleHandle || 'bundle';

  const pricing = /** @type {HTMLElement|null} */ (root.querySelector('[data-pricing]'));
  const regularEl = root.querySelector('[data-regular]');
  const discountEl = root.querySelector('[data-discount]');
  const totalEl = root.querySelector('[data-total]');
  const cartBtn = /** @type {HTMLButtonElement|null} */ (root.querySelector('[data-cart-btn]'));
  const successEl = /** @type {HTMLElement|null} */ (root.querySelector('[data-success]'));
  const defaultBtnLabel = cartBtn ? cartBtn.textContent : 'Add to Cart';

  let submitting = false;

  /**
   * @param {string} group
   * @returns {HTMLElement|null}
   */
  function selected(group) {
    return root.querySelector(`.bundle-picker__swatch--active[data-group="${group}"]`);
  }

  /** @param {string} group */
  function selectedPrice(group) {
    const active = selected(group);
    return active ? parseInt(active.dataset.price || '0', 10) || 0 : 0;
  }

  function update() {
    const complete = GROUPS.every((group) => selected(group) != null);

    for (const group of GROUPS) {
      const priceEl = root.querySelector(`[data-price-for="${group}"]`);
      if (priceEl) priceEl.textContent = selected(group) ? formatMoney(selectedPrice(group)) : '';
    }

    if (pricing) pricing.hidden = !complete;
    if (cartBtn) cartBtn.disabled = !complete;
    if (!complete) return;

    const sellingTotal = selectedPrice('a') + selectedPrice('b');
    // Match the original block: floor the saving so the shopper is never charged
    // a fraction of a cent more than the displayed total.
    const savings = Math.floor((sellingTotal * discountPct) / 100);

    if (regularEl) regularEl.textContent = formatMoney(sellingTotal);
    if (discountEl) discountEl.textContent = `-${formatMoney(savings)}`;
    if (totalEl) totalEl.textContent = formatMoney(sellingTotal - savings);
  }

  root.querySelectorAll('.bundle-picker__swatch').forEach((el) => {
    const btn = /** @type {HTMLElement} */ (el);
    btn.addEventListener('click', () => {
      const group = btn.dataset.group;
      root.querySelectorAll(`.bundle-picker__swatch[data-group="${group}"]`).forEach((sibling) => {
        sibling.classList.remove('bundle-picker__swatch--active');
        sibling.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add('bundle-picker__swatch--active');
      btn.setAttribute('aria-pressed', 'true');
      if (successEl) successEl.classList.remove('bundle-picker__success--visible');
      update();
    });
  });

  async function add() {
    const selA = selected('a');
    const selB = selected('b');
    if (submitting || !selA || !selB || !cartBtn) return;

    const sellingTotal = selectedPrice('a') + selectedPrice('b');
    const savings = Math.floor((sellingTotal * discountPct) / 100);
    const bundleTotal = (sellingTotal - savings) / 100;

    submitting = true;
    cartBtn.disabled = true;
    cartBtn.setAttribute('aria-busy', 'true');
    cartBtn.textContent = 'ADDING...';

    const properties = {
      _sgbundle_id: `${bundleHandle}-${Date.now()}`,
      _sgbundle_parent: String(parentVariant),
      _sgbundle_product: String(productId),
      _sgbundle_price: bundleTotal.toFixed(2),
    };

    const items = [selA, selB]
      .map((el) => parseInt(el.dataset.variantId || '', 10))
      .filter((id) => Number.isFinite(id))
      .map((id) => ({ id, quantity: 1, properties: { ...properties } }));

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add the bundle to your cart.');
      }
      const cart = await res.json();

      if (successEl) successEl.classList.add('bundle-picker__success--visible');
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: cart, sourceId: 'bundle-picker', data: { source: 'bundle-picker' } },
        })
      );
      const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      } else {
        window.location.href = '/cart';
      }
    } catch (error) {
      console.error('[bundle-picker] add to cart failed', error);
    } finally {
      submitting = false;
      cartBtn.removeAttribute('aria-busy');
      cartBtn.textContent = defaultBtnLabel;
      update();
    }
  }

  if (cartBtn) cartBtn.addEventListener('click', add);

  update();
}

function initAll() {
  document.querySelectorAll('.bundle-picker').forEach((el) => {
    if (el instanceof HTMLElement) initBundlePicker(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}
